import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  customerControllerGetCustomerQueryOptions,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatZonedDateTime } from '../../lib/date-formatting';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function RecapLine({ label, value }: { label: string; value?: string }) {
  if (!value) {
    return null;
  }
  return (
    <Group align="baseline" justify="between" className="border-b py-2 last:border-b-0">
      <Text inline size="sm" tone="muted">
        {label}
      </Text>
      <Text inline size="sm" weight="medium">
        {value}
      </Text>
    </Group>
  );
}

/**
 * What a stop request asks for, read the same way by the requester confirming it and the manager
 * validating it — the two must never describe the same request differently.
 */
export function StopRequestSummary({
  reason,
  effectiveAt,
  requestedBy,
}: {
  reason: string;
  effectiveAt?: string;
  requestedBy?: { name?: string; businessRoleId?: string };
}) {
  const { t } = useTranslation();
  const { policy, branchIdForPolicy } = usePolicyOnDashboard();

  const customerInformations = usePolicyProductConfig('customerInformations');

  // The summary must identify who is being terminated, not just which contract.
  const { data: insured } = useQuery({
    ...customerControllerGetCustomerQueryOptions(policy.customers[0] ?? '', {
      branchId: branchIdForPolicy,
    }),
    enabled: !!policy.customers[0],
  });

  // Policyholders can't read the firm: asking would only 403 and toast over the confirm step.
  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['GET_BROKERAGE_FIRM'],
  });
  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(policy.brokerageFirm ?? ''),
    enabled: !!policy.brokerageFirm && can.GET_BROKERAGE_FIRM,
  });

  const insuredName =
    (insured && customerDisplayName([insured], customerInformations?.fieldsConfig)) || undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('stopFunnel.recap.summaryTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <RecapLine label={t('stopFunnel.recap.insured')} value={insuredName} />
        <RecapLine label={t('stopFunnel.recap.policy')} value={policy.externalId} />
        <RecapLine
          label={t('stopFunnel.recap.product')}
          value={t(`products.${policy.productId}.label`, { defaultValue: policy.productId })}
        />
        <RecapLine label={t('stopFunnel.recap.broker')} value={brokerageFirm?.companyInfo?.name} />
        <RecapLine
          label={t('stopFunnel.recap.reason')}
          value={t([`${policy.productId}.stopReasons.${reason}`, `stopReasons.${reason}`, reason])}
        />
        <RecapLine
          label={t('stopFunnel.recap.effectiveAt')}
          value={
            effectiveAt ? formatZonedDateTime(new Date(effectiveAt), policy.timeZone) : undefined
          }
        />
        <RecapLine
          label={t('stopFunnel.recap.requestedBy')}
          value={
            requestedBy?.name ??
            (requestedBy?.businessRoleId
              ? t(`businessRoles.${requestedBy.businessRoleId}.label`, {
                  defaultValue: requestedBy.businessRoleId,
                })
              : undefined)
          }
        />
      </CardContent>
    </Card>
  );
}
